// Audio examples for Style suggestion values.
//
// Every instrumentation / percussion suggestion value (see PromptHints::Library
// DIMENSION_SUGGESTIONS) maps to ONE canonical "sample key". A short CC0 clip for
// that key lives at /audio/samples/<key>.mp3 (provenance in public/audio/samples/PROVENANCE.json).
//
// Resolution is two-stage. FIRST, an exact match against the instrument catalog
// (INSTRUMENT_FAMILIES) — every catalog instrument owns a unique key, so "violin",
// "viola", "tenor sax" each resolve to their own clip. SECOND, for everything else
// (the genre-flavoured combo phrases like "dusty Rhodes piano"), ORDERED keyword rules:
// the first rule whose pattern matches the (lowercased) value wins, specific -> general.
//
// AVAILABLE_SAMPLE_KEYS is the subset of keys that currently have an audio file shipped.
// The UI only renders a play button when resolveSampleKey(value) is in this set, so
// keys we haven't sourced yet simply show no button (no broken players).
import { INSTRUMENT_FAMILIES } from './instrumentFamilies'
import { GENRE_FAMILIES } from './genreFamilies'

export interface SampleRule {
  pattern: RegExp
  key: string
}

// Human-readable label per key (tooltip / aria-label on the play button).
export const SAMPLE_LABELS: Record<string, string> = {
  // --- instruments ---
  acoustic_guitar: 'acoustic guitar',
  nylon_guitar: 'nylon-string guitar',
  electric_guitar: 'electric guitar',
  bass_guitar: 'electric bass',
  double_bass: 'upright bass',
  sub_bass: '808 / sub bass',
  grand_piano: 'grand piano',
  electric_piano: 'electric piano (Rhodes)',
  organ: 'Hammond organ',
  synth_pad: 'analog synth pad',
  synth_lead: 'synth lead',
  synth_arp: 'synth arpeggio',
  synth_bass: 'synth bass',
  synth_acid: 'acid 303 line',
  strings: 'string section',
  cello: 'cello',
  brass: 'brass section',
  trumpet: 'muted trumpet',
  saxophone: 'saxophone',
  harmonica: 'harmonica',
  harp: 'harp',
  bells: 'glockenspiel / bells',
  celesta: 'celesta',
  marimba: 'marimba',
  vibraphone: 'vibraphone',
  choir: 'choir',
  flute: 'flute / woodwind',
  pedal_steel: 'pedal steel guitar',
  fiddle: 'fiddle',
  banjo: 'banjo',
  harpsichord: 'harpsichord',
  clavinet: 'clavinet',
  accordion: 'accordion',
  mandolin: 'mandolin',
  ukulele: 'ukulele',
  kalimba: 'kalimba',
  sitar: 'sitar',
  koto: 'koto',
  steel_pan: 'steel pan',
  bagpipes: 'bagpipes',
  dulcimer: 'hammered dulcimer',
  // --- catalog instruments (own clip each) ---
  upright_piano: 'upright piano',
  honky_tonk_piano: 'honky-tonk piano',
  wurlitzer: 'Wurlitzer electric piano',
  fm_piano: 'FM electric piano',
  music_box: 'music box',
  pipe_organ: 'pipe organ',
  harmonium: 'harmonium',
  twelve_string: '12-string guitar',
  electric_guitar_overdrive: 'overdriven electric guitar',
  jazz_guitar: 'jazz electric guitar',
  resonator: 'resonator slide guitar',
  slap_bass: 'slap bass',
  fretless_bass: 'fretless bass',
  violin: 'violin',
  viola: 'viola',
  pizzicato: 'pizzicato strings',
  muted_trumpet: 'muted trumpet',
  trombone: 'trombone',
  french_horn: 'French horn',
  tuba: 'tuba',
  flugelhorn: 'flugelhorn',
  piccolo: 'piccolo',
  clarinet: 'clarinet',
  oboe: 'oboe',
  cor_anglais: 'cor anglais',
  bassoon: 'bassoon',
  soprano_sax: 'soprano sax',
  alto_sax: 'alto sax',
  baritone_sax: 'baritone sax',
  pan_flute: 'pan flute',
  recorder: 'recorder',
  xylophone: 'xylophone',
  tubular_bells: 'tubular bells',
  shamisen: 'shamisen',
  erhu: 'erhu',
  oud: 'oud',
  tin_whistle: 'tin whistle',
  shakuhachi: 'shakuhachi',
  bandoneon: 'bandoneon',
  balalaika: 'balalaika',
  square_lead: 'square-wave lead',
  synth_brass: 'synth brass',
  synth_strings: 'synth strings',
  bell_synth: 'bell synth',
  choir_pad: 'choir pad',
  vocal_oohs: 'vocal oohs',
  gospel_choir: 'gospel choir',
  // --- percussion ---
  kick: 'kick drum',
  snare: 'snare drum',
  hihat: 'closed hi-hat',
  hihat_open: 'open hi-hat',
  ride_cymbal: 'ride cymbal',
  crash_cymbal: 'crash cymbal',
  toms: 'toms',
  claps: 'hand claps',
  snaps: 'finger snaps',
  shaker: 'shaker',
  tambourine: 'tambourine',
  cajon: 'cajón',
  conga: 'congas',
  bongos: 'bongos',
  claves: 'claves',
  rimshot: 'rimshot',
  taiko: 'taiko drum',
  timpani: 'timpani',
  breakbeat: 'breakbeat',
  boombap_drums: 'boom-bap drums',
  live_drums: 'live drum kit',
  brushed_drums: 'brushed drums',
  drum_machine: 'drum machine',
  dembow: 'reggaeton dembow',
  timbale: 'timbales',
}

// Ordered: specific patterns BEFORE general ones. First match wins.
export const SAMPLE_RULES: SampleRule[] = [
  // -- keys / pianos (electric before acoustic) --
  { pattern: /rhodes|electric piano|wurlitzer/, key: 'electric_piano' },
  { pattern: /clavinet|\bclav\b/, key: 'clavinet' },
  { pattern: /harpsichord/, key: 'harpsichord' },
  { pattern: /accordion|bandoneon/, key: 'accordion' },
  { pattern: /hammond|\borgan\b/, key: 'organ' },
  { pattern: /music box/, key: 'celesta' },
  { pattern: /celesta/, key: 'celesta' },
  { pattern: /piano/, key: 'grand_piano' },

  // -- bass (specific synthetic/acoustic before generic bass) --
  { pattern: /808/, key: 'sub_bass' },
  { pattern: /sub[- ]?bass/, key: 'sub_bass' },
  { pattern: /upright bass|double bass/, key: 'double_bass' },
  { pattern: /fm bass|synth bass/, key: 'synth_bass' },
  { pattern: /fretless|slap bass|bass guitar|bassline|\bbass\b/, key: 'bass_guitar' },

  // -- synths (specific synth flavours before generic) --
  { pattern: /acid|303/, key: 'synth_acid' },
  { pattern: /arp|arpeggio/, key: 'synth_arp' },
  { pattern: /supersaw|saw lead|synth lead|\blead\b/, key: 'synth_lead' },
  { pattern: /pad|analog synth|poly synth|\bsynths?\b|drone/, key: 'synth_pad' },

  // -- guitars (distorted/electric flavours before acoustic; nylon before acoustic) --
  { pattern: /nylon|classical guitar/, key: 'nylon_guitar' },
  { pattern: /pedal steel/, key: 'pedal_steel' },
  { pattern: /distort|crunch|gritty|buzzsaw|power chord|palm-mut|shred|down-tuned|overdriv|wah|slide guitar|electric guitar|rhythm guitar|jazz guitar|twangy/, key: 'electric_guitar' },
  { pattern: /acoustic guitar|fingerpicked|strummed guitar|12-?string|twelve-?string/, key: 'acoustic_guitar' },
  { pattern: /mandolin/, key: 'mandolin' },
  { pattern: /ukulele|\buke\b/, key: 'ukulele' },
  { pattern: /banjo/, key: 'banjo' },
  { pattern: /fiddle/, key: 'fiddle' },

  // -- strings / orchestral --
  { pattern: /cello/, key: 'cello' },
  { pattern: /violin|viola/, key: 'strings' },
  { pattern: /string/, key: 'strings' },
  { pattern: /trumpet/, key: 'trumpet' },
  { pattern: /brass|horn|trombone|tuba/, key: 'brass' },
  { pattern: /sax/, key: 'saxophone' },
  { pattern: /flute|woodwind|clarinet|oboe|bassoon|cor anglais|english horn|shakuhachi|tin whistle|penny whistle/, key: 'flute' },
  { pattern: /harmonica/, key: 'harmonica' },
  { pattern: /harp\b/, key: 'harp' },
  { pattern: /choir/, key: 'choir' },

  // -- tuned percussion / bells --
  { pattern: /marimba|xylophone/, key: 'marimba' },
  { pattern: /vibraphone|vibes/, key: 'vibraphone' },
  { pattern: /glockenspiel|bell/, key: 'bells' },

  // -- world / ethnic (new keys; clips still to be sourced) --
  { pattern: /kalimba|thumb piano|mbira/, key: 'kalimba' },
  { pattern: /sitar/, key: 'sitar' },
  { pattern: /\bkoto\b|shamisen/, key: 'koto' },
  { pattern: /steel ?pan|steel drum/, key: 'steel_pan' },
  { pattern: /bagpipe/, key: 'bagpipes' },
  { pattern: /dulcimer/, key: 'dulcimer' },

  // -- drum kits & electronic percussion (specific grooves before generic) --
  { pattern: /boom[- ]?bap/, key: 'boombap_drums' },
  { pattern: /breakbeat|amen|two-step|dnb|drum and bass|174|172|170 bpm/, key: 'breakbeat' },
  { pattern: /dembow/, key: 'dembow' },
  { pattern: /linndrum|drum machine|909|808 machine/, key: 'drum_machine' },
  { pattern: /brushed/, key: 'brushed_drums' },
  { pattern: /taiko|trailer drum/, key: 'taiko' },
  { pattern: /timpani/, key: 'timpani' },

  // -- single-hit percussion --
  { pattern: /open hi-?hat/, key: 'hihat_open' },
  { pattern: /hi-?hat|hats/, key: 'hihat' },
  { pattern: /ride/, key: 'ride_cymbal' },
  { pattern: /crash|cymbal/, key: 'crash_cymbal' },
  { pattern: /rim/, key: 'rimshot' },
  { pattern: /\btoms?\b/, key: 'toms' },
  { pattern: /snare/, key: 'snare' },
  { pattern: /kick|four-on-the-floor|double bass|double-kick|blast beat/, key: 'kick' },
  { pattern: /clap/, key: 'claps' },
  { pattern: /snap/, key: 'snaps' },
  { pattern: /shaker/, key: 'shaker' },
  { pattern: /tambourine/, key: 'tambourine' },
  { pattern: /cajón|cajon/, key: 'cajon' },
  { pattern: /conga/, key: 'conga' },
  { pattern: /bongo/, key: 'bongos' },
  { pattern: /clave/, key: 'claves' },
  { pattern: /timbale/, key: 'timbale' }, // after conga: "congas and timbales" -> conga

  // -- generic live-kit catch-alls (LAST: only if nothing specific matched) --
  { pattern: /live drums|backbeat|drum groove|drum kit|downbeat|eighth-note/, key: 'live_drums' },
]

// Keys that currently have an audio file shipped under public/audio/samples/<key>.mp3.
// The UI shows a play button only for keys in here, so unsourced keys simply show no
// button (never a broken player). Add a key here as soon as its Suno-generated clip is
// dropped in — see public/audio/samples/GENERATION.md for the prompt + filename per key.
export const AVAILABLE_SAMPLE_KEYS = new Set<string>([
  // Suno-generated (owner), normalized to -16 LUFS. Add a key here as each clip lands.
  'grand_piano', 'acoustic_guitar', 'nylon_guitar', 'electric_guitar', 'electric_piano',
  'organ', 'bass_guitar', 'sub_bass', 'synth_arp', 'synth_acid', 'synth_lead', 'synth_pad',
  'strings', 'cello', 'harmonica', 'flute', 'fiddle', 'banjo', 'vibraphone', 'choir',
  'celesta', 'harp', 'trumpet', 'saxophone', 'pedal_steel', 'bells', 'marimba', 'double_bass', 'synth_bass',
  'live_drums', 'boombap_drums', 'kick', 'hihat', 'hihat_open', 'drum_machine', 'snare', 'toms',
  'ride_cymbal', 'brushed_drums', 'taiko', 'timpani', 'dembow', 'shaker', 'tambourine', 'cajon',
  'bongos', 'claves', 'timbale', 'brass', 'conga', 'snaps', 'claps', 'breakbeat', 'rimshot',
  'crash_cymbal',
  // new instruments
  'harpsichord', 'clavinet', 'accordion', 'mandolin', 'ukulele', 'sitar', 'koto', 'steel_pan',
  'bagpipes', 'dulcimer', 'kalimba',
])

// Exact catalog name -> own key (built once). Normalized like suggestionInfo's `norm` so
// "Tenor sax" / "tenor-sax" / "tenor  sax" all match the catalog entry.
const catalogNorm = (s: string) => s.toLowerCase().replace(/[\s_&-]+/g, '')
const CATALOG_KEY_BY_NAME = new Map<string, string>()
for (const fam of INSTRUMENT_FAMILIES) {
  for (const e of fam.entries) CATALOG_KEY_BY_NAME.set(catalogNorm(e.value), e.key)
}

// Resolve a suggestion value string to a canonical sample key, or null if none matches.
// Catalog instruments win first (each owns a unique key); otherwise fall back to keyword rules.
export function resolveSampleKey(value: string): string | null {
  const exact = CATALOG_KEY_BY_NAME.get(catalogNorm(value))
  if (exact) return exact
  const v = value.toLowerCase()
  for (const rule of SAMPLE_RULES) {
    if (rule.pattern.test(v)) return rule.key
  }
  return null
}

// --- Genre demos --------------------------------------------------------------------
// EVERY unique genre owns its own clip. Each GENRE_FAMILIES entry carries a per-genre `key`
// (see genreFamilies.ts): spelling variants of one genre share a key ("lo-fi"/"lofi"/"low-fi"
// -> lofi), but distinct subgenres each get their own ("delta blues" -> delta_blues). A value
// resolves to its key by EXACT catalog lookup (mirroring instruments), so unlike the old
// family-keyword rules, "delta blues" no longer collapses onto the generic "blues" clip.
// Clips live at /audio/genres/<key>.mp3; a value previews only once its own key ships.
const GENRE_KEY_BY_NAME = new Map<string, string>()
export const GENRE_LABELS: Record<string, string> = {}
for (const fam of GENRE_FAMILIES) {
  for (const e of fam.entries) {
    GENRE_KEY_BY_NAME.set(catalogNorm(e.value), e.key)
    // First label wins per key — the headline spelling listed first in the family.
    if (!(e.key in GENRE_LABELS)) GENRE_LABELS[e.key] = e.value
  }
}

// Per-genre keys whose demo clip is shipped under /audio/genres/<key>.mp3. The UI shows a ▶
// only for keys in here, so a genre without its own clip yet shows NO button (never a wrong /
// shared one). Add a key here as each clip lands — see public/audio/genres/sound-bank.html.
export const AVAILABLE_GENRE_KEYS = new Set<string>([
  'lofi', 'synthwave', 'drum_and_bass', 'dubstep', 'trap', 'hip_hop', 'house', 'techno', 'trance',
  'edm', 'synth_funk', 'disco', 'rnb', 'gospel', 'afrobeats', 'kpop', 'hyperpop', 'pop', 'blues',
  'jazz', 'country', 'acoustic', 'reggae', 'latin', 'world', 'classical', 'cinematic', 'ambient',
  'industrial', 'experimental', 'rock', 'folk', 'metal', 'punk',
])

// Resolve a genre value to its own canonical key, or null if it isn't a known catalog genre.
export function resolveGenreKey(value: string): string | null {
  return GENRE_KEY_BY_NAME.get(catalogNorm(value)) ?? null
}

// Which kind of audio a value can preview is decided by the DIMENSION it came from, not by
// the words it contains. Instrument/percussion clips only for the instrumentation & percussion
// dimensions; genre demos only for the genre dimension. Every other dimension (mood, tempo,
// vocals, production, dynamics, structure, harmony) gets NO audio — even when a value happens
// to contain an instrument/genre word ("choir backing vocals", "frantic 174 BPM", "polished
// pop master"). Without this scoping those would all light up a misleading play button, and the
// word would also belong to two dimensions at once. A null/unknown dimension previews nothing.
const SAMPLE_DIMENSIONS = new Set(['instrumentation', 'percussion'])

export function sampleUrlFor(value: string, dimension: string | null): string | null {
  if (dimension && SAMPLE_DIMENSIONS.has(dimension)) {
    const key = resolveSampleKey(value)
    if (key && AVAILABLE_SAMPLE_KEYS.has(key)) return `/audio/samples/${key}.mp3`
  } else if (dimension === 'genre') {
    const genre = resolveGenreKey(value)
    if (genre && AVAILABLE_GENRE_KEYS.has(genre)) return `/audio/genres/${genre}.mp3`
  }
  return null
}

export function sampleLabelFor(value: string, dimension: string | null): string | null {
  if (dimension && SAMPLE_DIMENSIONS.has(dimension)) {
    const key = resolveSampleKey(value)
    if (key) return SAMPLE_LABELS[key] ?? key.replace(/_/g, ' ')
  } else if (dimension === 'genre') {
    const genre = resolveGenreKey(value)
    if (genre) return GENRE_LABELS[genre] ?? genre.replace(/_/g, ' ')
  }
  return null
}

// One shared <audio> so a new preview cuts off the previous one (no overlapping clips).
let current: HTMLAudioElement | null = null
let currentUrl: string | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of listeners) fn()
}

/** URL of the sample playing right now, or null. */
export function playingUrl(): string | null {
  return currentUrl
}

/** Subscribe to play/stop changes; returns an unsubscribe fn. */
export function subscribePlayback(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function stopSample(): void {
  if (current) {
    current.pause()
    current.currentTime = 0
    current = null
  }
  if (currentUrl !== null) {
    currentUrl = null
    notify()
  }
}

export function playSample(url: string): void {
  if (typeof Audio === 'undefined') return
  if (current) {
    current.pause()
    current.currentTime = 0
  }
  current = new Audio(url)
  currentUrl = url
  // Reset state when the clip finishes on its own so the button flips back to ▶.
  current.addEventListener('ended', () => {
    if (currentUrl === url) stopSample()
  })
  // Swallow autoplay/availability errors — a failed preview shouldn't surface to the user.
  current.play().catch(() => {})
  notify()
}

/** Play if not this url, stop if it already is. */
export function toggleSample(url: string): void {
  if (currentUrl === url) stopSample()
  else playSample(url)
}
